import { useCallback, useState } from 'react';

import { Typography } from '@mui/material';

import { AgentPickerInput, IconSelectorInput, ParameterTypes } from '@@common';

import { ComponentMetadataModel, ParameterMetadata, ParameterType, ReactCodeComponentModel } from '../../../api/api';
import { GenericProperties } from '../../FlowDesigner/ActionPanel/GenericProperties';
import ComponentInput from '../../FlowDesigner/Components/Inputs/ComponentInput';
import EmbeddedResourceInput from '../../FlowDesigner/Components/Inputs/EmbeddedResourceInput';
import EnumerationInput from '../../FlowDesigner/Components/Inputs/EnumerationInput';
import GenericPropertyInput from '../../FlowDesigner/Components/Inputs/GenericPropertyInput';
import SharedComponentInput from '../../FlowDesigner/Components/Inputs/SharedComponentInput';
import ViewInput from '../../FlowDesigner/Components/Inputs/ViewInput';
import { VariableScope } from '../../FlowDesigner/Models/VariableScopes/VariableScope';
import useUserInterfaceMetadata from '../Hooks/useUserInterfaceMetadata';
import { ComponentTypes } from '../Models/ComponentTypes';
import { IComponentModel } from '../Models/IComponentModel';
import { filterTreeManagedParameters } from '../Utils/ComponentTreeUtils';
import { createDefaultVirtualCollectionItem } from '../Utils/VirtualComponentCollectionUtils';
import ActionsInput from './Inputs/ActionsInput';
import ColorInput from './Inputs/ColorInput';
import DataDisplayPropertiesInput from './Inputs/DataDisplayPropertiesInput';
import InputContentChip from './Inputs/InputContentChip';
import LayoutInput from './Inputs/LayoutInput';
import ManagedChatInput from './Inputs/ManagedChatInput';
import ReactFilesInput from './Inputs/ReactFilesInput';
import OperationsInput from './Inputs/OperationsInput';
import { ReactComponentBindingInput } from './Inputs/ReactComponentBindingInput';
import { SortableCollapsibleItem } from './Inputs/SortableCollapsibleItem';
import { SortableItemListDialog } from './Inputs/SortableItemListDialog';
import TableColumnsInput from './Inputs/TableColumnsInput';
import TabsItemsInput from './Inputs/TabsItemsInput';

type ComponentPropertiesProps = {
	component: IComponentModel;
	metadata: ComponentMetadataModel;
	reactComponents?: ReactCodeComponentModel[];
	variableScope?: VariableScope;
	onComponentChanged: (updated: IComponentModel) => void;
};

type VirtualComponentCollectionInputProps = {
	items: Record<string, unknown>[] | undefined;
	onItemsChange: (items: Record<string, unknown>[] | undefined) => void;
	parameterMetadata: ParameterMetadata;
	virtualMetadata: ComponentMetadataModel;
};

function getVirtualItemTitle(item: Record<string, unknown>, index: number, metadata: ComponentMetadataModel) {
	const label = item.Label ?? item.Name ?? item.Title;
	return typeof label === 'string' && label.trim().length > 0 ? label : `${metadata.displayName || metadata.name} ${index + 1}`;
}

function VirtualComponentCollectionInput({ items, onItemsChange, parameterMetadata, virtualMetadata }: VirtualComponentCollectionInputProps) {
	const [open, setOpen] = useState(false);
	const count = items?.length ?? 0;
	const itemLabel = count === 1 ? virtualMetadata.displayName || virtualMetadata.name : `${virtualMetadata.displayName || virtualMetadata.name}s`;

	return (
		<GenericPropertyInput value={items} onValueChange={onItemsChange} onOpen={() => setOpen(true)}>
			{count > 0 && <InputContentChip label={`${count} ${itemLabel}`} />}
			{open && (
				<SortableItemListDialog<Record<string, unknown>>
					open={open}
					setOpen={setOpen}
					title={`Configure ${parameterMetadata.displayName || parameterMetadata.name}`}
					items={items}
					onItemsChange={(updatedItems) => onItemsChange(updatedItems.length > 0 ? updatedItems : undefined)}
					createItem={() => createDefaultVirtualCollectionItem(virtualMetadata)}
					addLabel={`Add ${virtualMetadata.displayName || virtualMetadata.name}`}
				>
					{(item, index, total, onChange, onMoveUp, onMoveDown, onDelete) => (
						<SortableCollapsibleItem
							key={index}
							title={getVirtualItemTitle(item, index, virtualMetadata)}
							subtitle={typeof item.Field === 'string' ? item.Field : undefined}
							index={index}
							total={total}
							onMoveUp={onMoveUp}
							onMoveDown={onMoveDown}
							onDelete={onDelete}
						/>
					)}
				</SortableItemListDialog>
			)}
		</GenericPropertyInput>
	);
}

export function ComponentProperties({ component, metadata, reactComponents = [], variableScope, onComponentChanged }: ComponentPropertiesProps) {
	const { componentByName } = useUserInterfaceMetadata();
	const parameters = (metadata.isContainer ? filterTreeManagedParameters(metadata.parameters ?? []) : (metadata.parameters ?? [])).map((parameter) =>
		component.Type === ComponentTypes.ReactComponentHost && parameter.name === 'Chat' ? { ...parameter, iconName: 'Chat' } : parameter,
	);

	const renderPropertyInput = useCallback(
		(parameterMetadata: ParameterMetadata, value: any, onValueChange: (value: any) => void) => {
			if (component.Type === ComponentTypes.ReactComponentHost && parameterMetadata.name === 'Files') {
				return <ReactFilesInput value={value} onValueChange={onValueChange} />;
			}
			if (component.Type === ComponentTypes.ReactComponentHost && parameterMetadata.name === 'Chat') {
				return <ManagedChatInput value={value} onValueChange={onValueChange} />;
			}

			if (component.Type === ComponentTypes.ReactComponentHost && parameterMetadata.name === 'ComponentName') {
				return (
					<ReactComponentBindingInput
						componentId={component.Id}
						componentName={typeof value === 'string' ? value : undefined}
						components={reactComponents}
						onComponentNameChange={onValueChange}
					/>
				);
			}

			const virtualMetadata = parameterMetadata.typeCustom ? componentByName.get(parameterMetadata.typeCustom) : undefined;
			if (parameterMetadata.isCollection && virtualMetadata?.isVirtual === true) {
				return (
					<VirtualComponentCollectionInput
						items={Array.isArray(value) ? value : undefined}
						onItemsChange={onValueChange}
						parameterMetadata={parameterMetadata}
						virtualMetadata={virtualMetadata}
					/>
				);
			}

			// Check for standard UI-specific parameter types
			if (parameterMetadata.type === ParameterType.ComponentRef) {
				return <ComponentInput componentRef={value} onComponentRefChange={onValueChange} />;
			}
			if (parameterMetadata.type === ParameterType.SharedComponentRef) {
				return <SharedComponentInput sharedComponentRef={value} onSharedComponentRefChange={onValueChange} />;
			}
			if (parameterMetadata.type === ParameterType.EmbeddedResourceRef) {
				return <EmbeddedResourceInput embeddedResourceRef={value} onEmbeddedResourceRefChange={onValueChange} />;
			}
			if (parameterMetadata.type === ParameterType.OperationRef && parameterMetadata.isCollection) {
				return <OperationsInput operations={value} onOperationsChange={onValueChange} />;
			}

			// Check for custom parameter type to render specialized input
			if (parameterMetadata.typeCustom === ParameterTypes.IconName) {
				return <IconSelectorInput value={value} onValueChange={onValueChange} label="" variant="standard" />;
			}

			if (parameterMetadata.typeCustom === ParameterTypes.Color) {
				return <ColorInput color={value} onColorChange={onValueChange} />;
			}

			if (parameterMetadata.typeCustom === ParameterTypes.AgentId) {
				return <AgentPickerInput value={value} onValueChange={onValueChange} />;
			}

			if (parameterMetadata.typeCustom === ParameterTypes.TableColumns) {
				return <TableColumnsInput columns={value} onColumnsChange={onValueChange} />;
			}

			if (parameterMetadata.typeCustom === ParameterTypes.TabsItems) {
				return <TabsItemsInput tabs={value} onTabsChange={onValueChange} />;
			}

			if (parameterMetadata.typeCustom === ParameterTypes.DataDisplayProperty) {
				return <DataDisplayPropertiesInput properties={value} onPropertiesChange={onValueChange} variableScope={variableScope} />;
			}

			if (parameterMetadata.typeCustom === ParameterTypes.Actions) {
				return <ActionsInput actions={value} onActionsChange={onValueChange} />;
			}

			if (parameterMetadata.typeCustom === ParameterTypes.ViewId) {
				return <ViewInput viewRef={value} onViewRefChange={onValueChange} />;
			}

			if (parameterMetadata.typeCustom === ParameterTypes.Layout) {
				return <LayoutInput layout={value} onLayoutChange={onValueChange} />;
			}

			if (parameterMetadata.typeCustom === ParameterTypes.EnumRef) {
				return <EnumerationInput enumRef={value} onEnumRefChange={onValueChange} />;
			}

			return null;
		},
		[component, componentByName, reactComponents, variableScope],
	);

	if (parameters.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary">
				No configurable properties for {metadata.displayName}.
			</Typography>
		);
	}

	return (
		<GenericProperties<IComponentModel>
			name={metadata.displayName || component.Type}
			id={component.Id}
			parameters={parameters}
			model={component}
			onModelChanged={onComponentChanged}
			variableScope={variableScope}
		>
			{renderPropertyInput}
		</GenericProperties>
	);
}
